<script setup lang="ts">
import { computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import AccountCreationForm from "@/components/auth/AccountCreation/AccountCreationForm.vue";
import SessionBanner from "@/components/RegistrationProcess/apply/SessionBanner.vue";
import { LOGIN_REDIRECT_QUERY } from "@/components/RegistrationProcess/constants";
import ArrowLeft from "@/components/shared/icons/ArrowLeft.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import type { Props } from "./types";

defineOptions({
  name: "AuthGuardWithAccountCreation",
});

const props = withDefaults(defineProps<Props>(), {});

const store = useStore();
const route = useRoute();
const router = useRouter();

const session = computed<Record<string, any>>(() => props.session?.value);
const isLoggedIn = computed<boolean>(() => store.getters.isLoggedIn);
const isBranded = computed<boolean>(() => Boolean(store?.getters?.getBrandingDetails?.secondary_color));

const isUnauthorizedApplicationModification = computed<boolean>(
  () => route.name === "modify-application" && !isLoggedIn.value,
);

const setRedirectDestination = (flagRedirection: boolean = false): void => {
  const redirectRoute = JSON.stringify({
    path: route.path,
    params: route.params,
    query: { ...route.query, [LOGIN_REDIRECT_QUERY]: flagRedirection ? "true" : undefined },
  });
  localStorage.setItem("redirectRoute", redirectRoute);
};

const clearRedirectDestination = (): void => {
  localStorage.removeItem("redirectRoute");
};

const redirectToLogin = (): void => {
  setRedirectDestination();
  void router.push({ name: "sign-in" });
};

watch(
  isUnauthorizedApplicationModification,
  (shouldRedirect) => {
    if (shouldRedirect) {
      redirectToLogin();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    v-if="!isLoggedIn"
    class="mx-auto mb-8 flex w-full flex-col gap-6 p-4 md:block md:w-1/3 md:gap-0 md:p-0"
  >
    <router-link
      :to="{ name: 'results' }"
      id="back-to-results-link"
      class="flex items-center md:mt-6"
      :class="[ isBranded ? 'brandingSecondaryText' : 'text-university-secondary']"
    >
      <ArrowLeft custom-class="w-4 h-4" />
      <span class="font-bold text-xs">
        Back to Results Page
      </span>
    </router-link>
    <session-banner data-test='session-banner' :session="session" />
    <div class="flex flex-col w-full px-4 md:px-0">
      <router-link
        data-test='login-link'
        :to="{ name: 'sign-in' }"
        @click="setRedirectDestination(true)"
        @contextmenu="setRedirectDestination(true)"
        class="flex justify-center items-center w-full text-center rounded font-semibold border-2 border-transparent px-4 text-white hover:text-white focus:text-white min-h-[58px] md:mt-8 md:min-h-[60px]"
        :class="[
          isBranded
            ? 'brandingSecondary'
            : 'bg-university-secondary hover:bg-teal-100',
        ]"
      >
        <span class="pr-1 uppercase">
          Login
        </span>
        <ArrowRight custom-class="w-4 h-4" :currentColor="'white'" />
      </router-link>
      <div class="my-0 flex items-center gap-3 md:my-8">
        <hr class="flex-grow border-indigo-base" />
        <span class="text-lg font-medium text-indigo-base whitespace-nowrap">or</span>
        <hr class="flex-grow border-indigo-base" />
      </div>
      <p class="mb-0 text-lg font-medium">
        Begin your journey by signing up below
      </p>
      <account-creation-form
        data-test='account-creation-form'
        action-button-copy="Create Account &amp; Begin Registration"
        @after-creation-action="clearRedirectDestination"
      />
    </div>
  </div>

  <!-- Authenticated content -->
  <div v-else class="w-full"> <!-- Without this div, Vue will throw a warning if the slotted content doesn't have a single root element -->
    <slot></slot>
  </div>
</template>
